// Core
import useSWR from "swr";

// Definitions
import { GameInitRequestType, GameInitType } from "@/domains/games/types";
import {
  HttpRequestParams,
  HttpResponseAxiosDataType,
  HttpResponseAxiosErrorType,
  ServerErrorBaseType,
} from "@/types/http";

// Utils
import { gamesApi } from "@/domains/games/service";
import { gamesKeys } from "@/domains/games/apiKeys";
import { log } from "@/utils/browser-logger";
import { useStore } from "@/hooks/use-store";

type UseGameInitType = {
  loadingGameInit: boolean;
  gameUrl: string;
  onMutateGameInit: () => void;
  errorGameInit?: ServerErrorBaseType;
};

const fetcher = (data: HttpRequestParams<GameInitRequestType>) => () => gamesApi.initGame({ data });

export const useGameInit = (payload: GameInitRequestType): UseGameInitType => {
  const { user } = useStore();
  const { gameId } = payload;

  const key = gameId && user?.isAuth ? [gamesKeys.initGame, { gameId, token: user.token }] : null;

  const { data, isLoading, isValidating, mutate, error } = useSWR<
    HttpResponseAxiosDataType<GameInitType>,
    HttpResponseAxiosErrorType
  >(
    key,
    fetcher({
      data: { gameId },
      configOptions: { headers: { Authorization: `Bearer ${user?.token}` } },
    }),
    {
      revalidateOnMount: false, // чтобы не вызывалось сразу
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      onError: (err: HttpResponseAxiosErrorType) => {
        try {
          const status = err.response?.status;
          if (status >= 400 && status < 600) {
            log.crucial(err.message);
          }
        } catch (e) {
          log.crucial(err.message);
        }
      },
    },
  );

  const onMutateGameInit = () => {
    return void mutate(void 0, { revalidate: true });
  };

  return {
    loadingGameInit: isLoading || isValidating,
    gameUrl: data?.data.url || "",
    onMutateGameInit,
    errorGameInit: error?.response?.data,
  };
};
